import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { Building2, FileText, Download, Edit, X } from 'lucide-react';
import DocumentPreview from '../../../Components/Ui/DocumentPreview';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useGetSellerByIdQuery, useUpdateSellerMutation } from '../../../app/services/sellerApi';
import Button from '../../../Components/Ui/Button';
import useFileUpload from '../../../hooks/fileUpload';

// Added imports for ApplyForBrands logic
import Card from '../../../Components/Ui/Card';
import {
  useGetAllBrandsQuery,
  useGetUserInfoQuery,
  useApplyForBrandsMutation,
} from '../../../app/services/authApi';

const SellerProfile = () => {
  const { user: userInfo } = useSelector((state) => state.userInfo, shallowEqual);
  const isBrandAndSeller =
    (userInfo?.role ?? []).includes('brand') && (userInfo?.role ?? []).includes('seller');
  const [isEditing, setIsEditing] = useState(false);

  const { data, isLoading, refetch } = useGetSellerByIdQuery(userInfo?._id);
  const [updateSeller, { isLoading: isUpdating }] = useUpdateSellerMutation();
  // eslint-disable-next-line no-unused-vars
  const { uploadFile, uploading } = useFileUpload();
  const [gstFile, setGstFile] = useState(null);
  const [showGstPreviewModal, setShowGstPreviewModal] = useState(false);
  const [formData, setFormData] = useState({
    tradeName: '',
    ownerName: '',
    gstNumber: '',
    gstCertificate: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipcode: '',
  });

  // ---------------- ApplyForBrands states/hooks ----------------
  const { data: brandsData, isLoading: brandsLoading } = useGetAllBrandsQuery();
  const { data: userBrandsInfo, isLoading: userLoading } = useGetUserInfoQuery(userInfo?._id);
  // eslint-disable-next-line no-unused-vars
  const [applyForBrands, { isLoading: isApplying }] = useApplyForBrandsMutation();

  const [selectedBrands, setSelectedBrands] = useState([]);
  const [sellerAuthCertificates, setSellerAuthCertificates] = useState({});
  const [fileError, setFileError] = useState('');
  const [gstError, setGstError] = useState('');

  const validateGstNumber = (gst) => {
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstRegex.test(gst);
  };

  const existingBrandIds = useMemo(() => {
    return new Set(userBrandsInfo?.data?.brandDeal?.map((deal) => deal.brand) || []);
  }, [userBrandsInfo]);

  const availableBrands = useMemo(() => {
    return brandsData?.data.filter((brand) => !existingBrandIds.has(brand._id)) || [];
  }, [brandsData, existingBrandIds]);

  // eslint-disable-next-line no-unused-vars
  const handleBrandChange = (e) => {
    const brandId = e.target.value;
    const isChecked = e.target.checked;
    if (isChecked) {
      setSelectedBrands([...selectedBrands, brandId]);
    } else {
      setSelectedBrands(selectedBrands.filter((id) => id !== brandId));
      const newAuthCerts = { ...sellerAuthCertificates };
      delete newAuthCerts[brandId];
      setSellerAuthCertificates(newAuthCerts);
    }
  };

  const handleBrandFileChange = (e, brandId) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSizeMB = 50;
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];

    const brand = availableBrands.find((b) => b._id === brandId);
    if (!validTypes.includes(file.type)) {
      toast.error('Only PDF, JPEG, or PNG files are allowed.');
      return;
    }

    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`File size should not exceed ${maxSizeMB} MB.`);
      setFileError(`File too large for ${brand?.name || 'this brand'}`);
      return;
    }

    setSellerAuthCertificates({ ...sellerAuthCertificates, [brandId]: file });
    toast.success(`File for ${file.name} uploaded successfully!`);
  };

  const handleBrandSubmit = async (e) => {
    e.preventDefault();
    if (selectedBrands.length === 0) {
      toast.warning('Please select at least one brand to apply for.');
      return;
    }

    try {
      const brandDeals = await Promise.all(
        selectedBrands.map(async (brandId) => {
          let certificate = '';
          if (sellerAuthCertificates[brandId]) {
            certificate = await uploadFile(sellerAuthCertificates[brandId]);
          }
          return { brand: brandId, certificate };
        }),
      );

      await applyForBrands({ id: userInfo._id, brandDeals }).unwrap();
      toast.success('Successfully applied for new brands!');
      setSelectedBrands([]);
      setSellerAuthCertificates({});
    } catch (error) {
      toast.error('Failed to apply for new brands.');
    }
  };

  useEffect(() => {
    if (data?.data) {
      const seller = data.data;
      setFormData({
        tradeName: seller.tradeName || '',
        ownerName: seller.name || '',
        gstNumber: seller.gstNumber || '',
        gstCertificate: seller.gstRegistrationCertificate?.[0] || '',
        addressLine1: seller.primaryAddress?.line1 || '',
        addressLine2: seller.primaryAddress?.line2 || '',
        city: seller.primaryAddress?.city || '',
        state: seller.primaryAddress?.state || '',
        zipcode: seller.primaryAddress?.zipCode || '',
      });
    }
  }, [data]);

  // useEffect(() => {
  //   return () => {
  //     if (gstFile) {
  //       URL.revokeObjectURL(formData.gstCertificate);
  //     }
  //   };
  // }, [gstFile, formData.gstCertificate]);

  const handleDownloadCertificate = () => {
    if (formData?.gstCertificate) {
      window.open(formData.gstCertificate, '_blank');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (data?.data) {
      const seller = data.data;
      setFormData({
        tradeName: seller.tradeName || '',
        ownerName: seller.name || '',
        gstNumber: seller.gstNumber || '',
        gstCertificate: seller.gstRegistrationCertificate?.[0] || '',
        addressLine1: seller.primaryAddress?.line1 || '',
        addressLine2: seller.primaryAddress?.line2 || '',
        city: seller.primaryAddress?.city || '',
        state: seller.primaryAddress?.state || '',
        zipcode: seller.primaryAddress?.zipCode || '',
      });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSizeMB = 50; // unified to 50MB
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];

    if (!validTypes.includes(file.type)) {
      toast.error('Only PDF, JPEG, or PNG files are allowed.');
      setFileError('Invalid file type');
      return;
    }

    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`File size should not exceed ${maxSizeMB} MB.`);
      setFileError('File too large');
      return;
    }

    // ✅ valid file
    const previewUrl = URL.createObjectURL(file);
    setFormData((prev) => ({
      ...prev,
      gstCertificate: previewUrl,
    }));
    setGstFile(file);
    setFileError(''); // clear error
  };

  const handleSave = async () => {
    if (formData.gstNumber && !validateGstNumber(formData.gstNumber)) {
      setGstError('Invalid GST Number format');
      toast.error('Please enter a valid GST Number.');
      return;
    } else {
      setGstError('');
    }
    try {
      let gstUrl = formData.gstCertificate;

      if (gstFile) {
        gstUrl = await uploadFile(gstFile);
        if (!gstUrl) {
          toast.error('File upload failed.');
          return;
        }
      }

      const payload = {
        tradeName: formData.tradeName,
        name: formData.ownerName,
        gstNumber: formData.gstNumber,
        gstRegistrationCertificate: gstUrl ? [gstUrl] : [],
        primaryAddress: {
          line1: formData.addressLine1,
          line2: formData.addressLine2,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipcode,
        },
      };

      await updateSeller({ id: userInfo._id, body: payload }).unwrap();
      toast.success('Seller profile updated successfully!');
      setIsEditing(false);
      refetch();
      setGstFile(null);
    } catch (err) {
      toast.error(err?.message || 'Failed to update seller profile');
    }
  };

  if (isLoading || brandsLoading || userLoading) {
    return (
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8">
        <p className="text-center text-gray-600">Loading seller data...</p>
      </div>
    );
  }

  if (!data?.data) {
    return (
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8">
        <p className="text-center text-gray-600">No seller data available.</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8 relative">
        <div className="absolute top-1 right-8">
          {!isEditing ? (
            <Edit className="cursor-pointer text-primary" onClick={() => setIsEditing(true)} />
          ) : (
            <X className="cursor-pointer text-red-500" onClick={handleCancel} />
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="h-full flex flex-col">
            <div className="flex-1 secondary-background p-6 rounded-lg border border-color">
              <div className="flex items-center mb-4">
                <Building2 className="h-5 w-5 text-primary mr-2" />
                <h3 className="text-lg font-semibold text-gray-800">Information</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Trade Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="tradeName"
                      value={formData.tradeName || ''}
                      onChange={handleChange}
                      className="form-input w-full border rounded p-2"
                    />
                  ) : (
                    <div className="text-gray-900">{formData.tradeName || 'N/A'}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Owner Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="ownerName"
                      value={formData.ownerName || ''}
                      onChange={handleChange}
                      className="form-input w-full border rounded p-2"
                    />
                  ) : (
                    <div className="text-gray-900">{formData.ownerName || 'N/A'}</div>
                  )}
                </div>

                <div>
                  {!isEditing && (
                    <label className="block text-sm font-medium text-primary mb-1">Address</label>
                  )}

                  {isEditing ? (
                    <div className="space-y-2">
                      <div>
                        <label className="block text-sm font-medium text-primary mb-1 px-2">
                          Address 1
                        </label>
                        <input
                          type="text"
                          name="addressLine1"
                          placeholder="Address Line 1"
                          value={formData.addressLine1 || ''}
                          onChange={handleChange}
                          className="form-input w-full border rounded p-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-primary mb-1 px-2">
                          Address 2
                        </label>
                        <input
                          type="text"
                          name="addressLine2"
                          placeholder="Address Line 2"
                          value={formData.addressLine2 || ''}
                          onChange={handleChange}
                          className="form-input w-full border rounded p-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-primary mb-1 px-2">
                          City
                        </label>
                        <input
                          type="text"
                          name="city"
                          placeholder="City"
                          value={formData.city || ''}
                          onChange={handleChange}
                          className="form-input w-full border rounded p-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-primary mb-1 px-2">
                          State
                        </label>
                        <input
                          type="text"
                          name="state"
                          placeholder="State"
                          value={formData.state || ''}
                          onChange={handleChange}
                          className="form-input w-full border rounded p-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-primary mb-1 px-2">
                          Zipcode
                        </label>
                        <input
                          type="text"
                          name="zipcode"
                          placeholder="Zip Code"
                          value={formData.zipcode || ''}
                          onChange={handleChange}
                          className="form-input w-full border rounded p-2"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="text-gray-900">
                      {[
                        formData.addressLine1,
                        formData.addressLine2,
                        formData.city,
                        formData.state,
                        formData.zipcode,
                      ]
                        .filter(Boolean)
                        .join(', ') || 'N/A'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="h-full flex flex-col">
            <div className="flex-1 secondary-background p-6 rounded-lg border border-color">
              <div className="flex items-center mb-4">
                <FileText className="h-5 w-5 text-primary mr-2" />
                <h3 className="text-lg font-semibold text-gray-800">GST details</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-primary mb-1 pl-3">
                    GST Number
                  </label>
                  {isEditing ? (
                    <div>
                      <input
                        type="text"
                        name="gstNumber"
                        value={formData.gstNumber || ''}
                        onChange={handleChange}
                        className={`form-input w-full border rounded p-2 ${
                          gstError ? 'border-red-500' : ''
                        }`}
                      />
                      {gstError && <p className="text-red-500 text-sm mt-1">{gstError}</p>}
                    </div>
                  ) : (
                    <div className="text-gray-900 font-mono text-lg bg-white p-3 rounded border">
                      {formData.gstNumber || 'N/A'}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-1 pl-3">
                    GST Registration Certificate
                  </label>
                  {isEditing ? (
                    <div className="space-y-2">
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileChange}
                        className="form-input w-full border rounded p-2"
                      />

                      {(gstFile || formData.gstCertificate) && (
                        <div
                          className="mt-2 border p-2 rounded bg-white cursor-pointer"
                          onClick={() => setShowGstPreviewModal(true)}
                        >
                          {(gstFile && gstFile.type === 'application/pdf') ||
                          (formData.gstCertificate && formData.gstCertificate.endsWith('.pdf')) ? (
                            <div className="flex items-center space-x-2">
                              <FileText className="text-red-500" />
                              <span className="text-primary underline">Preview PDF</span>
                            </div>
                          ) : (
                            <img
                              src={formData.gstCertificate}
                              alt="GST Certificate Preview"
                              className="max-h-48 object-contain rounded"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center space-x-3 bg-white p-3 rounded border">
                      <FileText className="h-5 w-5 text-gray-400" />
                      <span className="text-gray-900 flex-1 truncate">
                        {gstFile?.name ||
                          (formData.gstCertificate
                            ? formData.gstCertificate.split('/').pop()
                            : 'No certificate available')}
                      </span>
                      {formData.gstCertificate && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDownloadCertificate}
                          className="flex items-center space-x-1"
                        >
                          <Download className="h-4 w-4" />
                          <span>Download</span>
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 secondary-background p-6 rounded-lg border border-color">
            <div className="flex items-center mb-4">
              <Building2 className="h-5 w-5 text-primary mr-2" />
              <h3 className="text-lg font-semibold text-gray-800">Login Details</h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Email ID</label>
                <div className="text-gray-900 font-medium">{userInfo?.email || 'N/A'}</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">Phone Number</label>
                <div className="text-gray-900 font-medium">{userInfo?.phone || 'N/A'}</div>
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                variant="primary"
                size="sm"
                onClick={() => window.location.replace('/change-password')}
                className="flex items-center justify-end space-x-1"
              >
                <span>Change Password</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {isEditing && (
        <div className="flex justify-end mt-6 space-x-4">
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isUpdating || fileError}>
            {isUpdating ? 'Saving...' : 'Save'}
          </Button>
        </div>
      )}

      {!isBrandAndSeller && (
        <Card className="mt-4">
          <form onSubmit={handleBrandSubmit} className="space-y-6">
            {/* Already Selected Brands */}
            {userBrandsInfo?.data?.brandDeal?.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-800 pb-2 border-b-2 border-orange-200 mb-4">
                  Your Selected Brands
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    ...new Map(
                      userBrandsInfo.data.brandDeal.map((deal) => [deal.brand, deal]),
                    ).values(),
                  ].map((deal) => {
                    const brand = brandsData?.data.find((b) => b._id === deal.brand);
                    if (!brand) return null;

                    return (
                      <div
                        key={deal.brand}
                        className="flex flex-col items-start p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center w-full justify-between">
                          <div className="flex items-center">
                            {brand.brandLogo && (
                              <img
                                src={brand.brandLogo}
                                alt={`${brand.name} Logo`}
                                className="w-12 h-12 rounded-full mr-3 object-contain border border-gray-200 p-1"
                              />
                            )}
                            <span className="text-lg font-semibold text-gray-800">
                              {brand.name}
                            </span>
                          </div>
                        </div>

                        {deal.certificate && (
                          <div className="mt-2 flex items-center space-x-2">
                            <FileText className="text-gray-400" />
                            <a
                              href={deal.certificate}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary underline text-sm"
                            >
                              View Certificate
                            </a>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <h3 className="text-lg font-semibold text-gray-800 pb-2 border-b-2 border-orange-200 mb-4">
                Available Brands
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {availableBrands.map((brand) => (
                  <div
                    key={brand._id}
                    className="flex items-center p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <label
                      htmlFor={`brand-${brand._id}`}
                      className="ml-3 flex items-center text-lg font-semibold text-gray-800 cursor-pointer"
                    >
                      {brand.brandLogo && (
                        <img
                          src={brand.brandLogo}
                          alt={`${brand.name} Logo`}
                          className="w-12 h-12 rounded-full mr-3 object-contain border border-gray-200 p-1"
                        />
                      )}
                      {brand.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {selectedBrands.length > 0 && (
              <div>
                <h3 className="text-xl font-semibold text-gray-800 pb-2 border-b-2 border-orange-200 mb-4">
                  Upload Authenticity Certificates
                </h3>
                <div className="space-y-4">
                  {selectedBrands.map((brandId) => {
                    const brand = availableBrands.find((b) => b._id === brandId);
                    return (
                      <div key={brandId}>
                        <label className="block text-base font-medium text-primary mb-1">
                          {brand.name}
                        </label>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleBrandFileChange(e, brandId)}
                          className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:secondary-background file:text-primary hover:file:bg-orange-100"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <p className="mt-6 text-lg text-primary italic border-t pt-4">
              <span className="font-semibold">Note:</span> To apply for brands please contact admin.
            </p>
            {/* 
          <div className="flex justify-end">
            <Button type="submit" disabled={uploading || isApplying || !!fileError}>
              {uploading
                ? 'Uploading...'
                : isApplying
                  ? 'Applying...'
                  : 'Apply for Selected Brands'}
            </Button>
          </div> */}
          </form>
        </Card>
      )}

      {showGstPreviewModal && (
        <DocumentPreview
          isOpen={showGstPreviewModal}
          fileUrl={formData.gstCertificate}
          onClose={() => setShowGstPreviewModal(false)}
          fileType={formData.gstCertificate ? formData.gstCertificate.split('.').pop() : ''}
        />
      )}
    </>
  );
};

export default SellerProfile;
